import type { RawPacket, RawPackets } from '@frontend/core'
import { WebSocketController } from '@frontend/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRawWebsocketUrl } from '../../modules/servers'
import { useAppSelector } from '../../store/hooks'

export const useHardware = () => {
  const hardware = useAppSelector((state) => state.hardware)

  return hardware
}

export const useFormattedHardware = () => {
  const hardware = useHardware()

  return hardware.formattedHardware
}

export const useHardwareOnlineOrAsleep = () => {
  // Anchors
  const numberOfAnchorsOnline = useAppSelector(
    (state) => state.hardware.diagnostics.status.online.anchors.length
  )
  const numberOfAnchorsAsleep = useAppSelector(
    (state) => state.hardware.diagnostics.status.sleep.anchors.length
  )

  // Tags
  const numberOfTagsOnline = useAppSelector(
    (state) => state.hardware.diagnostics.status.online.tags.length
  )
  const numberOfTagsAsleep = useAppSelector(
    (state) => state.hardware.diagnostics.status.sleep.tags.length
  )

  // Balls
  const numberOfBallsOnline = useAppSelector(
    (state) => state.hardware.diagnostics.status.online.balls.length
  )
  const numberOfBallsAsleep = useAppSelector(
    (state) => state.hardware.diagnostics.status.sleep.balls.length
  )

  return {
    numberOfAnchorsOnline,
    numberOfAnchorsAsleep,
    numberOfTagsOnline,
    numberOfTagsAsleep,
    numberOfBallsOnline,
    numberOfBallsAsleep
  }
}

export const useHardwareSeatConfiguration = () => {
  const availableSeats = useAppSelector(
    (state) => state.hardware.diagnostics.availableSeats
  )

  const total = useAppSelector(
    (state) => state.metricsServer.metricsServerState.seatConfiguration?.total
  )
  const anchor = useAppSelector(
    (state) => state.metricsServer.metricsServerState.seatConfiguration?.anchor
  )
  const ball = useAppSelector(
    (state) => state.metricsServer.metricsServerState.seatConfiguration?.ball
  )
  const playerTag = useAppSelector(
    (state) =>
      state.metricsServer.metricsServerState.seatConfiguration?.playerTag
  )
  const lowPower = useAppSelector(
    (state) =>
      state.metricsServer.metricsServerState.seatConfiguration?.lowPower
  )

  return {
    availableSeats,
    total,
    anchor,
    ball,
    playerTag,
    lowPower
  }
}

export const useBallInPlay = () => {
  const formattedHardware = useFormattedHardware()

  const ballInPlay = useMemo(() => {
    return formattedHardware.types['ball'].devices.list.find(
      (item) => item.metrics.isInPlay.value
    )
  }, [formattedHardware])

  return { ballInPlay }
}

export const useRawWebsocketPacketCapture = () => {
  const [onlinePacketCapture, setOnlinePacketCapture] = useState<RawPackets>({})
  const [streamURL, path] = useRawWebsocketUrl()

  // Set up ref for interval
  const tagCheck = useRef<ReturnType<typeof setInterval> | null>(null)

  // Listen to raw websocket and update strack buffer once strack has been successfully initiated
  useEffect(() => {
    // Don't open a socket to an unresolved URL ('' would connect to the page
    // origin); wait until the real streaming URL is available.
    if (!streamURL) return

    const onlinePacketCapture: RawPackets = {},
      offlinePacketCapture: RawPackets = {}

    WebSocketController.connectWebSocket(
      streamURL,
      path,
      (data: RawPacket) => {
        // A raw-only packet carries no device state worth capturing
        if (!data.position) return
        if (!onlinePacketCapture[data.serial]) {
          delete offlinePacketCapture[data.serial]
        }
        onlinePacketCapture[data.serial] = data
        const timeNow = new Date().getTime() / 1000
        onlinePacketCapture[data.serial].packetArrivalTime = timeNow
      },
      'raw-packet-capture'
    )

    tagCheck.current = setInterval(() => {
      for (const tagId in onlinePacketCapture) {
        const timeNow = new Date().getTime() / 1000
        const tag = onlinePacketCapture[tagId]
        if (timeNow - tag.packetArrivalTime > 5) {
          offlinePacketCapture[tag.id] = tag
          delete onlinePacketCapture[tag.id]
        }
      }

      setOnlinePacketCapture(onlinePacketCapture)
    }, 2000)

    return () => {
      WebSocketController.disconnectWebSocket('raw', 'raw-packet-capture')

      if (tagCheck.current) clearInterval(tagCheck.current)
    }
  }, [streamURL, path])

  return { onlinePacketCapture }
}
